import { HttpClient, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, timer } from 'rxjs';
import { catchError, delayWhen, retryWhen, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CallBody } from '../models/call-body.model';
import { CallData } from '../models/call-data.model';
import { CallRateRequest } from '../models/call-rate-request.model';
import { Call } from '../models/call.model';
import { Doctor } from '../models/doctor.model';
import { FileLoadResponse } from '../models/file-load-response.model';
import { Patient } from '../models/patient.model';
import { Specialty } from '../models/specialty.model';
import { RequestService } from './request.service';
import { AppointmentService } from './appointment.service';
import { AmplitudeService } from './amplitude.service';
import { AmplitudeEvents } from '../enums/amplitude-events';
import { CallStatus } from '../enums/call-status.enum';
import { FacialScanService } from './facial-scan.service';
import { Coordinates } from '../models/coordinates.model';
import { CallReason } from '../models/call-reason.model';
import { Provider } from '../models/provider.model';
import _ from 'lodash';
import { LocaleService } from './locale.service';
import { ChatAI } from '../models/chat-ai.model';

@Injectable({
  providedIn: 'root'
})
export class CallService {
  private callDataSubject: BehaviorSubject<CallData>;
  private timeToRetryInMilliseconds = 30 * 1000;

  set callData(callData: CallData) {
    this.callDataSubject.next(callData);
  }

  get callData(): CallData {
    return this.callDataSubject.value;
  }

  get callData$(): Observable<CallData> {
    return this.callDataSubject.asObservable();
  }

  get doctor(): Doctor | null {
    return this.callData.doctor;
  }

  set doctor(doctor: Doctor | null) {
    this.callData.doctor = doctor;
    this.callDataSubject.next(this.callData);
  }

  get specialty(): Specialty | null {
    return this.callData.specialty;
  }

  set specialty(specialty: Specialty | null) {
    this.callData.specialty = specialty;
    this.callDataSubject.next(this.callData);
  }

  get caller(): Patient {
    return this.callData.caller;
  }

  set caller(caller: Patient) {
    this.callData.caller = caller;
    this.callDataSubject.next(this.callData);
  }

  get reason(): string | string[] {
    return this.callData.reason;
  }

  set reason(reason: string | string[]) {
    if (Array.isArray(reason)) {
      reason = reason.join(', ');
    }
    reason = reason.trim();
    this.callData.reason = reason;
    this.callDataSubject.next(this.callData);
  }

  get records(): string[] {
    return this.callData.records;
  }

  set records(records: string[]) {
    this.callData.records = records;
    this.callDataSubject.next(this.callData);
  }

  get coordinates(): Coordinates {
    return this.callData.coordinates;
  }

  set coordinates(coordinates: Coordinates) {
    this.callData.coordinates = coordinates;
    this.callDataSubject.next(this.callData);
  }

  get address(): string | undefined {
    return this.callData.address;
  }

  set address(address: string) {
    this.callData.address = address;
    this.callDataSubject.next(this.callData);
  }

  get chatAI(): ChatAI | null {
    return this.callData.chatAI;
  }

  set chatAI(chatAI: ChatAI | null) {
    this.callData.chatAI = chatAI;
    this.callDataSubject.next(this.callData);
  }

  private callSubject = new BehaviorSubject<Call | null>(null);

  get call$(): Observable<Call | null> {
    return this.callSubject.asObservable();
  }

  get call(): Call | null {
    return this.callSubject.value;
  }

  set call(call: Call | null) {
    localStorage.setItem('call', JSON.stringify(call));
    this.callSubject.next(call);
  }

  getCallId(call: string | Call): string {
    if (typeof call === 'string') {
      return call;
    }
    return call?.id;
  }

  hasMotive(): boolean {
    return localStorage.getItem('motive') ? true : false;
  }

  skipMedicalHistory(provider: Provider): boolean {
    return localStorage.getItem('skipMedicalHistory') === 'true' || provider.skipMedicalHistory;
  }

  set skipSelectCaller(skip: boolean) {
    localStorage.setItem('skipSelectCaller', JSON.stringify(skip));
  }

  get skipSelectCaller(): boolean {
    return localStorage.getItem('skipSelectCaller') === 'true';
  }

  get hasHearingDifficulties(): boolean | null {
    const hearingDifficulties = sessionStorage.getItem('hearing_difficulties');
    if (hearingDifficulties === null) return null;
    return hearingDifficulties === 'true';
  }

  set hasHearingDifficulties(value: boolean) {
    sessionStorage.setItem('hearing_difficulties', String(value));
  }

  constructor(
    private httpClient: HttpClient,
    private requestService: RequestService,
    private amplitudeService: AmplitudeService,
    private facialScanService: FacialScanService,
    private appointmentService: AppointmentService,
    private localeService: LocaleService
  ) {
    let callData: CallData | string | null = localStorage.getItem('call_data');
    if (callData) {
      callData = JSON.parse(callData);
    } else {
      callData = new CallData();
    }
    const call = localStorage.getItem('call')!;
    this.call = JSON.parse(call);

    this.callDataSubject = new BehaviorSubject<CallData>(callData as CallData);
    this.callData$.subscribe({
      next: (callData: CallData) => {
        localStorage.setItem('call_data', JSON.stringify(callData));
      }
    });
  }

  createCall(
    localeId: string | null,
    errorMessage: (message: string) => void,
    options?: { allowWithUnresolvedCalls?: boolean }
  ): Observable<Call> {
    if (this.doctor) {
      this.amplitudeService.showEvent(AmplitudeEvents.CALLING_BY_DOCTOR, {
        doctor: `${this.doctor.firstName} ${this.doctor.lastName}`
      });
      return this.processCall(this.createCallByDoctor(localeId));
    }
    if (this.specialty) {
      this.amplitudeService.showEvent(AmplitudeEvents.CALLING_BY_SPECIALTY, {
        specialty: `${this.specialty.name}`
      });
    }
    return this.processCall(this.createCallBySpecialty(localeId, errorMessage, options));
  }

  getCall(id: string): Promise<Call> {
    return this.requestService.get(`/call/${id}`, {});
  }

  getCallUrlPdfs(id: string): Promise<any> {
    return this.requestService.get(`/call/${id}/files`);
  }

  getDoctor(idCall: string): Promise<any> {
    return this.requestService.get(`/call/${idCall}/doctor`);
  }

  processCall(promise: Observable<Call>): Observable<Call> {
    return promise.pipe(
      tap((call: Call) => {
        this.call = call;
        if (!call.coordinates && this.coordinates) this.updateCoordinates(call.id, this.coordinates).then();
      })
    );
  }

  validateCall(id: string | Call = this.call!) {
    return this.requestService.put(`/call/${this.getCallId(id)}/validate`, {});
  }

  timeoutCall(id: string | Call = this.call!): Promise<Call> {
    return this.requestService.put(`/call/${this.getCallId(id)}/timeout`, {});
  }

  cancelCall(id: string | Call = this.call!): Promise<void> {
    return this.requestService.put(`/call/${this.getCallId(id)}/cancel`, {});
  }

  endCall(patientDuration: number, connectionLost: boolean, call: string | Call = this.call!): Promise<unknown> {
    return this.requestService.put(`/call/${this.getCallId(call)}/end`, {
      patientDuration,
      connectionLost
    });
  }

  rateCall(rateRequest: CallRateRequest, id: string | Call = this.call!, executeNow = false): Promise<void> {
    const body = { ...rateRequest, executeNow };
    if (!rateRequest.questions) {
      return this.requestService.put(`/call/${this.getCallId(id)}/rate`, body);
    } else {
      return this.rateCustomQuestions(rateRequest);
    }
  }

  endCallFlow(): void {
    this.callData = new CallData();
    this.call = null;
    localStorage.removeItem('call_data');
    sessionStorage.removeItem('currentStepIndex');
    sessionStorage.removeItem('steps');
    sessionStorage.removeItem('historySteps');
    sessionStorage.removeItem('firstShortFlowView');
    this.facialScanService.reset();
  }

  private rateCustomQuestions(rateRequest: CallRateRequest): Promise<void> {
    return this.requestService.post(`/satisfactionSurveyAnswers`, rateRequest);
  }

  uploadFile(file: FormData, id: string | Call = this.call!): Observable<HttpEvent<FileLoadResponse>> {
    return this.httpClient.put<FileLoadResponse>(`${environment.API_URL}/call/${this.getCallId(id)}/uploadFile`, file, {
      reportProgress: true,
      observe: 'events'
    });
  }

  private createCallByDoctor(localeId: string | null, options?: { allowWithUnresolvedCalls?: boolean }): Observable<Call> {
    const {
      caller: { id: patient },
      records,
      reason: motive,
      coordinates,
      address
    } = this.callData;

    const doctor = this.doctor!.id;
    const bodyParams: CallBody = {
      patient,
      records,
      motive,
      doctor,
      coordinates,
      address
    };

    if (localeId) {
      bodyParams.language = localeId;
    }

    if (options?.allowWithUnresolvedCalls) {
      bodyParams.allowWithUnresolvedCalls = true;
    }

    if (this.appointmentService.inAppointment()) {
      bodyParams.appointment = this.appointmentService.getAppointmentId()!;
      const appointmentSpecialty = this.appointmentService.specialty;
      if (appointmentSpecialty) {
        bodyParams.specialty = appointmentSpecialty.realId;
        this.callData.specialty = appointmentSpecialty;
      }
    }

    return this.httpClient.post<Call>(`${environment.API_URL}/call`, bodyParams);
  }

  private createCallBySpecialty(
    localeId: string | null,
    errorMessage: (message: string) => void,
    options?: { allowWithUnresolvedCalls?: boolean }
  ): Observable<Call> {
    const {
      caller: { id: patient },
      records,
      reason: motive,
      specialty,
      coordinates,
      address,
      chatAI
    }: CallData = this.callData;
    const specialtyId = specialty!.realId;

    const bodyParams: CallBody = {
      patient,
      records,
      motive,
      coordinates,
      address,
      ...(localeId && { language: localeId }),
      chatId: chatAI?.id,
      ...(options?.allowWithUnresolvedCalls && { allowWithUnresolvedCalls: true })
    };

    return this.httpClient.post<Call>(`${environment.API_URL}/call/specialty/${specialtyId}`, bodyParams).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 0 && error?.error instanceof ProgressEvent) errorMessage('init_call_error');
        return throwError(error);
      }),
      retryWhen((errors: Observable<HttpErrorResponse>) => {
        return errors.pipe(
          // Wait for 30 seconds
          delayWhen((httpError: HttpErrorResponse) => {
            const error = httpError?.error;
            // If the error is shouldResolveCall we should not retry here, but let the component handle it
            if (error?.name === 'shouldResolveCall') {
              throw error;
            }
            this.handlerError(error, errorMessage);
            console.log(`${error?.name}: ${error?.message}`);
            return timer(this.timeToRetryInMilliseconds);
          }),
          tap(() => {
            console.log('Retrying call creation...');
            window.top?.postMessage({ type: 'retrying_call_creation' }, '*');
          })
        );
      })
    );
  }

  handlerError(error: any, errorMessage?: (message: string) => void) {
    let eventType: AmplitudeEvents;
    let eventParams: any;
    if (errorMessage && (error?.name === 'cannotMakeCall' || error?.name == 'callLimitReached')) {
      errorMessage(error?.message);
    }
    if (error?.name === 'noAvailableDoctor') {
      eventType = AmplitudeEvents.CALL_BY_SPECIALTY_ERROR;
      eventParams = { message: error?.message };
    } else if (error?.name === 'doctorDND') {
      eventType = AmplitudeEvents.DOCTOR_NOT_AVAILABLE;
      eventParams = {
        doctor: `${this.doctor?.firstName} ${this.doctor?.lastName}`,
        callId: this.call?.id
      };
    } else {
      eventType = AmplitudeEvents.CALL_ERROR;
      eventParams = {
        error,
        callId: this.call?.id
      };
    }

    this.amplitudeService.showEvent(eventType, eventParams);
  }

  getCallStatus(callId: string): Promise<{ status: CallStatus }> {
    return this.requestService.get(`/call/${callId}/status`);
  }

  checkCallValidationStatus(): Promise<{ canMakeCall: boolean; reason?: string }> {
    return this.requestService.get(`/call/patient/canMakeSessionCall`);
  }

  reconnectPatient(call: Call): Promise<Call> {
    return this.requestService.post(`/call/${call.id}/reconnect`, {});
  }

  // In some cases it takes several seconds to get the coordinates, so we must send them after creating the call
  async updateCoordinates(callId: string, coordinates: Coordinates): Promise<void> {
    return this.requestService.put(`/call/${callId}/coordinates`, { coordinates });
  }

  getDefaultCallReasons(): { text: string; other?: boolean }[] {
    const defaultCallReasons = [
      { reasonLocaleKey: 'reason_fever' },
      { reasonLocaleKey: 'reason_cold/flu/breathingDifficulty' },
      { reasonLocaleKey: 'reason_headache' },
      { reasonLocaleKey: 'reason_earache' },
      { reasonLocaleKey: 'reason_soreThroat/cough' },
      { reasonLocaleKey: 'reason_abdominalPain' },
      { reasonLocaleKey: 'reason_vomiting/diarrhea' },
      { reasonLocaleKey: 'reason_other', other: true }
    ];

    return defaultCallReasons.map(({ reasonLocaleKey, other }) => ({ text: this.localeService.getString(reasonLocaleKey), other }));
  }

  getCustomCallReasons(): Promise<CallReason[]> {
    return this.requestService.get('/callReason');
  }

  async getCallReasons(provider: Provider): Promise<{ text: string; other?: boolean }[]> {
    if (!provider.usesCustomCallReasons) {
      return this.getDefaultCallReasons();
    }

    const customCallReasons = await this.getCustomCallReasons();
    if (_.isEmpty(customCallReasons)) return this.getDefaultCallReasons();

    const formattedCallReasons: { text: string; other?: boolean }[] = [];
    customCallReasons?.forEach((reason) => {
      const languageCode = Object.keys(reason.localizedText)[0];
      formattedCallReasons.push({
        text: reason.localizedText[languageCode]
      });
    });

    if (provider.allowOtherCallReasons) {
      const otherReason = this.getDefaultCallReasons().find((reason) => reason.other)!;
      formattedCallReasons.push(otherReason);
    }

    return formattedCallReasons;
  }

  getSpecialtiesUsedByPatient(patientId: string): Promise<Specialty[]> {
    return this.requestService.get(`/patient/${patientId}/specialties`);
  }
}
